import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { RegistrationStep } from '../constants/steps.js';

export type UserInfo = {
  readonly name: string;
  readonly companyName: string;
  readonly email: string;
  readonly phoneNumber: string;
};

export type AccessData = {
  readonly requestedAccessApplication: string;
  readonly requestedRole: string;
};

export type PasswordData = {
  readonly value: string;
  readonly confirmValue: string;
};

type RegistrationContextType = {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  userInfo: UserInfo;
  setUserInfo: (userInfo: UserInfo) => void;
  accessInfo: AccessData;
  setAccessInfo: (accessInfo: AccessData) => void;
  confirmationCode: string;
  setConfirmationCode: (code: string) => void;
  password: PasswordData;
  setPassword: (password: PasswordData) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | undefined;
  setError: (error: string | undefined) => void;
  completedSteps: number[];
  completeStep: (step: number) => void;
  isRegistrationComplete: boolean;
  otpExpiresAt: string | undefined;
  setOtpExpiresAt: (otpExpiresAt: string | undefined) => void;
  userCreated: boolean;
  setUserCreated: (userCreated: boolean) => void;
};

const RegistrationContext = createContext<RegistrationContextType | undefined>(undefined);

export const useRegistration = (): RegistrationContextType => {
  const context = useContext(RegistrationContext);
  if (context === undefined) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};

type RegistrationProviderProps = {
  readonly children: ReactNode;
};

export const RegistrationProvider = ({ children }: RegistrationProviderProps) => {
  const [currentStep, setCurrentStep] = useState(RegistrationStep.USER_INFO);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    name: '',
    companyName: '',
    email: '',
    phoneNumber: '',
  });
  const [accessInfo, setAccessInfo] = useState<AccessData>({
    requestedAccessApplication: '',
    requestedRole: '',
  });
  const [confirmationCode, setConfirmationCode] = useState('');
  const [password, setPassword] = useState<PasswordData>({ value: '', confirmValue: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [otpExpiresAt, setOtpExpiresAt] = useState<string | undefined>();
  const [userCreated, setUserCreated] = useState(false);

  const completeStep = useCallback((step: number) => {
    setCompletedSteps((previous) => (previous.includes(step) ? previous : [...previous, step]));
  }, []);

  const isRegistrationComplete = completedSteps.includes(RegistrationStep.CONFIRMATION_CODE);

  const value = useMemo<RegistrationContextType>(
    () => ({
      completeStep,
      completedSteps,
      accessInfo,
      confirmationCode,
      currentStep,
      error,
      isLoading,
      isRegistrationComplete,
      otpExpiresAt,
      password,
      setAccessInfo,
      setConfirmationCode,
      setCurrentStep,
      setError,
      setIsLoading,
      setOtpExpiresAt,
      setPassword,
      setUserCreated,
      setUserInfo,
      userCreated,
      userInfo,
    }),
    [
      completeStep,
      completedSteps,
      accessInfo,
      confirmationCode,
      currentStep,
      error,
      isLoading,
      isRegistrationComplete,
      setAccessInfo,
      otpExpiresAt,
      password,
      userCreated,
      userInfo,
    ]
  );

  return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>;
};
